import { useEffect, useState } from "react";
import styles from "./TimeTracking.module.css";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import WeekViewTable from "./WeekViewTable";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import Filter from "./Filter";
import DayViewTable from "./DayViewTable";
import Loader from "../../../components/Loader";
import { toast } from 'react-smart-toaster';
import { AddLogCallAPI } from "../../../components/AddLogs";
import { endOfPayWeek, startOfPayWeek } from "../../../utils/payWeek";
import { DEFAULT_CURRENCY, formatCurrencyDecimal, getCurrencySymbol } from "../../../helpers/currency";

dayjs.extend(isoWeek);

const formatMinutesToH = (totalMinutes) => {
  if (totalMinutes === 0)
    return 0;
  const hours = Math.floor(totalMinutes / 60);

  return `${hours}`;
}

const formatCurrencyNoDecimalIfInteger = (value, code = DEFAULT_CURRENCY) => {
  const symbol = getCurrencySymbol(code);
  const num = Number(value ?? 0);
  if (num % 1 === 0) {
    return `${symbol}${num.toLocaleString("en-US")}`;
  }
  return formatCurrencyDecimal(num, code);
}

export default function TimeTracking() {
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [weekOffset, setWeekOffset] = useState(0);
  const [dateRange, setDateRange] = useState({ start: startOfPayWeek(dayjs()).format('YYYY-MM-DD'), end: endOfPayWeek(dayjs()).format('YYYY-MM-DD') });
  const [dateRangeText, setDateRangeText] = useState('');
  const [filterData, setFilterData] = useState({ property: '0', role: '' });
  const [activeView, setActiveView] = useState("Week");
  const [widgetData, setWidgetData] = useState({ total_minutes: 0, total_wages: {}, total_emplyeee: 0, late_check_in: 0, missed_checkouts: 0 });

  useEffect(() => {
    const getUserList = async () => {
      setContentLoading(true);

      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      axios(process.env.REACT_APP_USER_API_URL + "v1/time-tracking-list", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { start_date: dateRange?.start, end_date: dateRange?.end }
      }).then((response) => {
        setContentLoading(false);
        setAllData(response.data.data);
      }).catch(() => {
        setContentLoading(false);
      });
    }

    getUserList();
  }, [getAccessTokenSilently, dateRange]);

  useEffect(() => {
    if (activeView === 'Day') {
      setDateRange({ start: dayjs(selectedDate).format('YYYY-MM-DD'), end: dayjs(selectedDate).format('YYYY-MM-DD') });
    }
    if (activeView === 'Week') {
      const refDate = dayjs().add(weekOffset, "week");
      setDateRange({ start: startOfPayWeek(refDate).format('YYYY-MM-DD'), end: endOfPayWeek(refDate).format('YYYY-MM-DD') });
    }
  }, [weekOffset, selectedDate, activeView]);

  useEffect(() => {
    if (dayjs(dateRange?.start)?.format('YYYY') === dayjs(dateRange?.end)?.format('YYYY')) {
      if (dayjs(dateRange?.start)?.format('MMM') === dayjs(dateRange?.end)?.format('MMM')) {
        setDateRangeText(dayjs(dateRange?.start)?.format('MMM') + ' ' + dayjs(dateRange?.start)?.format('D') + ' - ' + dayjs(dateRange?.end)?.format('D') + ', ' + dayjs(dateRange?.start)?.format('YYYY'));
      } else {
        setDateRangeText(dayjs(dateRange?.start)?.format('MMM') + ' ' + dayjs(dateRange?.start)?.format('D') + ' - ' + dayjs(dateRange?.end)?.format('MMM') + ' ' + dayjs(dateRange?.end)?.format('D') + ', ' + dayjs(dateRange?.start)?.format('YYYY'));
      }
    } else {
      setDateRangeText(dayjs(dateRange?.start)?.format('MMM') + ' ' + dayjs(dateRange?.start)?.format('D') + ', ' + dayjs(dateRange?.start)?.format('YYYY') + ' - ' + dayjs(dateRange?.end)?.format('MMM') + ' ' + dayjs(dateRange?.end)?.format('D') + ', ' + dayjs(dateRange?.end)?.format('YYYY'));
    }
  }, [dateRange]);



  useEffect(() => {
    let filteredDataTemp = [...allData];

    if (parseInt(filterData?.property) > 0) {
      filteredDataTemp = filteredDataTemp?.filter(i => i?.property_ids.includes(parseInt(filterData?.property)));
    }

    if (filterData?.role !== '' && filterData?.role !== 'all') {
      filteredDataTemp = filteredDataTemp?.filter(i => i?.role_id === filterData?.role);
    }

    setFilteredData(filteredDataTemp);
  }, [allData, filterData]);

  useEffect(() => {
    let total_minutes = 0;
    let total_wages = {};
    let total_emplyeee = 0;
    let late_check_in = 0;
    let missed_checkouts = 0;

    allData?.forEach(i => {
      const currency = i?.currency || DEFAULT_CURRENCY;

      i?.timeTracking.forEach(t => {
        total_minutes += t?.working_time;
        let wageTotal = 0;
        let otWageTotal = 0;

        if (i?.wage > 0 && t?.regular_minutes > 0) {
          wageTotal = (t?.regular_minutes / 60) * i?.wage;
          wageTotal = Math.round(wageTotal);
        }

        if (i?.ot_wage > 0 && t?.approved_ot_time > 0) {
          otWageTotal = (t?.approved_ot_time / 60) * i?.ot_wage;
          otWageTotal = Math.round(otWageTotal);
        }

        if ((parseInt(wageTotal) + parseInt(otWageTotal)) > 0) {
          if (!total_wages[currency]) {
            total_wages[currency] = { total: 0, regular: 0, ot: 0 };
          }
          total_wages[currency].total += wageTotal + otWageTotal;
          total_wages[currency].regular += wageTotal;
          total_wages[currency].ot += otWageTotal;
        }

        if (t?.late !== "") {
          late_check_in += 1;
        }

        if (t?.checkin_time && !t?.checkout_time) {
          missed_checkouts += 1;
        }
      });

      if (total_minutes > 0) {
        total_emplyeee += 1;
      }
    });

    setWidgetData(prev => {
      return { ...prev, total_minutes: total_minutes, total_wages: total_wages, total_emplyeee: total_emplyeee, late_check_in: late_check_in, missed_checkouts: missed_checkouts };
    })

  }, [allData]);

  const updateTimeTracking = (data) => {
    setAllData(prev => {
      return prev?.map(i => {
        if (i?.id === data?.user_id) {
          let timeTracking = i?.timeTracking?.map(si => {
            if (si?.date === data?.date) {
              return { ...si, approved_ot_time: data?.approved_ot_time, break_mintes: data?.break_mintes, break_times: data?.break_times, checkin_time: data?.checkin_time, checkout_time: data?.checkout_time, created_at: data?.created_at, date: data?.date, day: data?.day, id: data?.id, is_ot_approved: data?.is_ot_approved, late: data?.late, lunch_end_time: data?.lunch_end_time, lunch_start_time: data?.lunch_start_time, ot_time: data?.ot_time, regular_minutes: data?.regular_minutes, shift_minutes: data?.shift_minutes, updated_at: data?.updated_at, user_id: data?.user_id, user_name: data?.user_name, working_time: data?.working_time };
            }
            return si;
          });

          return { ...i, timeTracking: timeTracking };
        }
        return i;
      })
    })
  }

  const deleteTimeTracking = async (itemId) => {
    setLoading(true);

    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

    axios.delete(process.env.REACT_APP_USER_API_URL + 'v1/time-tracking/delete-time/' + itemId, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then(response => {
      setLoading(false);

      let item = response.data.data;

      setAllData(prev => {
        return prev.map(i => {
          if (i?.id === item?.user_id) {
            let timeTracking = i?.timeTracking?.map(t => {
              if (t?.date === item?.date) {
                return { ...t, day_off: 1, approved_ot_time: 0, break_mintes: 0, checkin_time: null, checkout_time: null, created_at: null, is_ot_approved: 0, late: "", ot_time: 0, regular_minutes: 0, shift_minutes: 0, updated_at: null, user_id: 0, working_time: 0 };
              }
              return t;
            });

            return { ...i, timeTracking: timeTracking };
          }

          return i;
        });
      });

      let logData = {
        'title': 'Track time has been deleted successfully.',
        'description': [
          'Employee name: ' + item?.user_name,
          'Date: ' + dayjs(item?.date).format('MM/DD/YYYY'),
        ]
      }
      AddLogCallAPI(logData, token);
    }).catch(error => {
      if (typeof error.response !== 'undefined')
        toast.error(error.response.data.message);
      setLoading(false);
    });
  }

  return (<>
    {loading && <Loader />}
    <div className={`${styles.page}`}>

      {/* ── Topbar ── */}
      <div className={`${styles.topbar}`}>
        <div>
          <div className={`${styles.pageEyebrow}`}>HR &amp; Workforce</div>
          <div className={`${styles.pageTitle}`}>
            Time Tracker
          </div>
        </div>
        <div className={`${styles.topbarRight}`}>
          <div className={`${styles.viewTabs}`}>
            {["Day", "Week"].map(v => (<button key={v} className={`${styles.vtab} ${activeView === v ? styles.vtabOn : ""}`} onClick={() => { setActiveView(v); setSelectedDate(dayjs().format('YYYY-MM-DD')); setWeekOffset(0); }}>{v}</button>))}
          </div>
          <div className={`${styles.weekNav}`}>
            <button className={`${styles.navBtn}`} aria-label="Prev" onClick={(e) => { setWeekOffset(e => e - 1); setSelectedDate(dayjs(selectedDate).subtract(1, 'day').format('YYYY-MM-DD')); }}>‹</button>
            <span className={`${styles.weekLbl}`}>{activeView === "Day" ? dayjs(selectedDate).format('MMM D, YYYY') : dateRangeText}</span>
            <button className={`${styles.navBtn}`} aria-label="Next" onClick={(e) => { setWeekOffset(e => e + 1); setSelectedDate(dayjs(selectedDate).add(1, 'day').format('YYYY-MM-DD')); }}>›</button>
          </div>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className={`${styles.stats}`}>
        <div className={`${styles.scard}`}>
          <div className={`${styles.scardLbl}`}>Total hours</div>
          <div className={`${styles.scardVal}`}>{formatMinutesToH(widgetData?.total_minutes)}</div>
        </div>
        <div className={`${styles.scard}`}>
          <div className={`${styles.scardLbl}`}>Total Wages</div>

          <div className={`${styles.wageSplit}`}>
            <div className={`${styles.wageBlock}`}>
              <div className={`${styles.wageChip}`}>🇺🇸 USD</div>
              <div className={`${styles.wageVal}`}>
                {formatCurrencyDecimal(widgetData?.total_wages?.USD?.total || 0, 'USD')}
                {/* {widgetData?.total_wages?.USD?.ot > 0 && (
                  <span className={`${styles.wageOt}`}>
                    +{formatCurrencyNoDecimalIfInteger(widgetData?.total_wages?.USD?.ot, 'USD')} OT
                  </span>
                )} */}
              </div>
              <div className={`${styles.wageSub}`}>
                Reg {formatCurrencyDecimal(widgetData?.total_wages?.USD?.regular || 0, 'USD')} &middot; OT {formatCurrencyDecimal(widgetData?.total_wages?.USD?.ot || 0, 'USD')}
              </div>
            </div>

            <div className={`${styles.wageBlock}`}>
              <div className={`${styles.wageChip} ${styles.wageChipInr}`}>🇮🇳 INR</div>
              <div className={`${styles.wageVal}`}>
                {formatCurrencyDecimal(widgetData?.total_wages?.INR?.total || 0, 'INR')}
                {/* {widgetData?.total_wages?.INR?.ot > 0 && (
                  <span className={`${styles.wageOt}`}>
                    +{formatCurrencyNoDecimalIfInteger(widgetData?.total_wages?.INR?.ot, 'INR')} OT
                  </span>
                )} */}
              </div>
              <div className={`${styles.wageSub}`}>
                Reg {formatCurrencyDecimal(widgetData?.total_wages?.INR?.regular || 0, 'INR')} &middot; OT {formatCurrencyDecimal(widgetData?.total_wages?.INR?.ot || 0, 'INR')}
              </div>
            </div>
          </div>

          {Object.entries(widgetData?.total_wages || {})
            .filter(([code]) => code !== 'USD' && code !== 'INR')
            .length > 0 && (
              <div className={`${styles.scardSub}`} style={{ marginTop: 8 }}>
                {Object.entries(widgetData.total_wages)
                  .filter(([code]) => code !== 'USD' && code !== 'INR')
                  .map(([code, amt]) => (
                    <span key={code} style={{ marginRight: 8 }}>
                      {formatCurrencyDecimal(amt?.total || 0, code)}
                    </span>
                  ))}
              </div>
            )}
        </div>
        <div className={`${styles.scard}`}>
          <div className={`${styles.scardLbl}`}>Employees tracked</div>
          <div className={`${styles.scardVal}`}>{widgetData?.total_emplyeee}</div>
        </div>
        <div className={`${styles.scard}`}>
          <div className={`${styles.scardLbl}`}>Late clock-ins</div>
          <div className={`${styles.scardVal} ${styles.scardValWarn}`}>{widgetData?.late_check_in}</div>
        </div>
        <div className={`${styles.scard}`}>
          <div className={`${styles.scardLbl}`}>Missed clock-outs</div>
          <div className={`${styles.scardVal} ${styles.scardValDanger}`}>{widgetData?.missed_checkouts}</div>
        </div>
      </div>


      <div className={`${styles.tblHeader}`}>
        {activeView === "Week" && <div className={`${styles.tblTitle}`}>Weekly timesheets</div>}
        {activeView === "Day" && <div className={`${styles.dvDayTitle}`}>{dayjs(selectedDate).format('dddd, MMM D, YYYY')}</div>}

        <Filter setFilterData={setFilterData.bind(this)} />
      </div>

      {/* ══ WEEK VIEW ══ */}
      {activeView === "Week" && (
        <>
          <WeekViewTable contentLoading={contentLoading} filteredData={filteredData} dateRange={dateRange} setLoading={setLoading.bind(this)} updateTimeTracking={updateTimeTracking.bind(this)} deleteTimeTracking={deleteTimeTracking.bind(this)} />

          <div className={`${styles.legend}`}>
            <span className={`${styles.legItem}`}><span className={`${styles.legIn}`}>9:00</span> clock-in</span>
            <span className={`${styles.legItem}`}><span className={`${styles.legOut}`}>5:00</span> clock-out</span>
            <span className={`${styles.legItem}`}><span className={`${styles.legDot}`} style={{ background: "#534AB7" }} />Normal</span>
            <span className={`${styles.legItem}`}><span className={`${styles.legDot}`} style={{ background: "#EF9F27" }} />Overtime</span>
            <span className={`${styles.legItem}`}><span className={`${styles.legDot}`} style={{ background: "#27500A" }} />OT approved</span>
            <span className={`${styles.legItem}`}><span className={`${styles.legDot}`} style={{ background: "#E24B4A" }} />Late</span>
            <span className={`${styles.legItem}`}><span className={`${styles.legDot}`} style={{ background: "#378ADD" }} />In progress</span>
            <span className={`${styles.legItem}`}>Click cell to review · + to add time</span>
          </div>
        </>
      )}

      {activeView === "Day" && (
        <>
          <DayViewTable contentLoading={contentLoading} filteredData={filteredData} setLoading={setLoading.bind(this)} updateTimeTracking={updateTimeTracking.bind(this)} deleteTimeTracking={deleteTimeTracking.bind(this)} />

          <div className={`${styles.legend}`}>
            <span className={`${styles.legItem}`}><span className={`${styles.legDot}`} style={{ background: "#EEEDFE", border: "1px solid #c5c0ee", display: "inline-block" }} /> Scheduled</span>
            <span className={`${styles.legItem}`}><span className={`${styles.legDot}`} style={{ background: "#534AB7" }} />Worked</span>
            <span className={`${styles.legItem}`}><span className={`${styles.legDot}`} style={{ background: "#EF9F27" }} />Overtime</span>
            <span className={`${styles.legItem}`}><span className={`${styles.legDot}`} style={{ background: "#27500A" }} />OT approved</span>
            <span className={`${styles.legItem}`}><span className={`${styles.legDot}`} style={{ background: "#E24B4A" }} />Late</span>
            <span className={`${styles.legItem}`}><span className={`${styles.legDot}`} style={{ background: "#378ADD" }} />In progress</span>
            <span className={`${styles.legItem}`}>Click any row to review</span>
          </div>
        </>
      )}
    </div>
  </>);
}